import { cleanLabelFromExpression } from './expression/cleanLabelFromExpression';

/**
 * Evalua una expresión en función del contexto.
 *
 * @param {object} scope el contexto (objeto) contra el cual se evaluara la expresión
 * @param {string} expression La expresión a evaluar
 * @param {object} [options] opciones
 * @param {boolean} [options.referenceError=true] Si false, no emite el error y devuelve returnDefault
 * @param {boolean} [options.typeError=true] Si false, no emite el error y devuelve returnDefault
 * @param {boolean} [options.returnDefault=expression] Valor devuelto en caso de error catcheado, por defecto la cadena "expression" original
 * @param {boolean} [options.noUndefined=false] Si true, devuelve '' en caso de que el valor evaluado sea undefined
 *
 * @return valor evaluado de la expresión
 * @throw Error con el "context" añadido al objeto Error
 *
 * @example parse(null, "'evaluo una cadena'") // -> "evaluo una cadena"
 * @example parse({VALUE1: 1, VALUE2: 2}, 'VALUE1 + VALUE2') // -> 3
 * @example parse({VALUE1: '1', VALUE2: '2'}, 'VALUE1 + VALUE2') // -> '12'
 * Puede lanzar una excepción:
 * @example parse(null, 'España') // -> error referenceError
 * @example parse(null, 'España', { referenceError: false }) // -> 'España'
 * @example parse(null, "'España'") // -> 'España' // eso es lo que realmente se debería hacer, poner la cadena entre comillas
 * Ojo con las cadenas no entre comillas, por ejemplo:
 * @example parse('2020-10-8') // -> 2002
 * @example parse("'2020-10-8'") // -> "2020-10-8"
 *
 */
export function parse(scope = {}, expression = '', options = {}) {
  scope = scope ?? {}; // protection null and undefined
  const { referenceError = true, typeError = true, returnDefault = expression, noUndefined = false } = options;
  if (isTrue(expression)) {
    return true;
  }

  if (isFalse(expression)) {
    return false;
  }

  /**
   * interpretamos el scope como lista de argumentos
   * @example { CAB: {...}, CONFIG: {} } -> "CAB, CONFIG"
   * Valida que los nombres sea correctos para ser argumentos de funcion javascript,
   * y emite un error si incorrecto. Por eso esta fuera del try.
   */
  const args = scopeToString(scope);

  try {
    const values = Object.values(scope);
    const expressionWithoutLabel = cleanLabelFromExpression(expression);
    const fn = new Function(args, `; return ${expressionWithoutLabel}`);
    const evaluated = fn.apply(null, values);

    if (noUndefined) {
      return typeof evaluated === 'undefined' ? returnDefault : evaluated;
    }
    return evaluated;
  } catch (e) {
    e.message = `${e.message} (evaluando expression "${expression}")`;
    e.context = scope;
    if ((e.name === 'TypeError' && typeError) || (e.name === 'ReferenceError' && referenceError)) {
      throw e;
    }

    return returnDefault;
  }
}

function isTrue(expression) {
  return (typeof expression === 'boolean' && expression) || expression === 'true';
}

function isFalse(expression) {
  return (typeof expression === 'boolean' && !expression) || expression === 'false';
}

/**
 * Transform scope in comalist of keys
 * @param {Object} scope
 * @returns
 */
function scopeToString(scope) {
  const args = Object.keys(scope).toString();
  if (args.includes('-')) {
    throw new Error(`Los nombres de variables deben respetar el formateo javascript, hay un error en esta lista: "${args}" (Por ejemplo. "-" no esta permitido)`);
  }

  return args;
}
